import { buildApiUrl } from '~/app/lib/api-config';
import type {
  GetKmsDocumentStatusResponse,
  ListLinkedKmsDocumentsResponse,
  ListKmsHitlQueueResponse,
  ResolveKmsHitlRequest,
  ResolveKmsHitlResponse,
  UploadKmsDocumentParams,
  UploadKmsDocumentResponse,
  UploadKmsDocumentsBatchResult,
} from '~/app/services/kms/kms-document.types';

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { message?: string | string[] };
      if (Array.isArray(body.message)) {
        message = body.message.join(', ');
      } else if (typeof body.message === 'string') {
        message = body.message;
      }
    } catch {
      // ignore non-JSON errors
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
}

/**
 * Always send organization scope when known.
 * Super Admin: required for tenant library (omit / platform = platform library).
 * Other roles: server ignores this and uses JWT org (safe no-op).
 */
function appendOrganizationScope(
  target: URLSearchParams | FormData,
  key: string,
  organizationId: string | null | undefined,
  isSuperAdmin: boolean,
): void {
  if (organizationId === null || organizationId === 'platform') {
    if (isSuperAdmin) {
      if (target instanceof URLSearchParams) {
        target.set(key, 'platform');
      } else {
        target.append(key, 'platform');
      }
    }
    return;
  }
  if (organizationId) {
    if (target instanceof URLSearchParams) {
      target.set(key, organizationId);
    } else {
      target.append(key, organizationId);
    }
  }
}

export const KmsDocumentService = {
  async upload(
    params: UploadKmsDocumentParams,
  ): Promise<UploadKmsDocumentResponse> {
    const form = new FormData();
    form.append('file', params.file);
    form.append('scope', params.scope);
    if (params.documentType) {
      form.append('document_type', params.documentType);
    }
    if (params.assetId) {
      form.append('asset_id', params.assetId);
    }
    appendOrganizationScope(
      form,
      'organization_id',
      params.organizationId,
      Boolean(params.isSuperAdmin),
    );

    const response = await fetch(buildApiUrl('/api/v1/kms/documents'), {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    return parseJson<UploadKmsDocumentResponse>(response);
  },

  /**
   * Uploads files sequentially — each triggers its own classify/extract/link pipeline.
   */
  async uploadMany(
    params: Omit<UploadKmsDocumentParams, 'file'> & { files: File[] },
  ): Promise<UploadKmsDocumentsBatchResult> {
    const succeeded: UploadKmsDocumentsBatchResult['succeeded'] = [];
    const failed: UploadKmsDocumentsBatchResult['failed'] = [];

    for (const file of params.files) {
      try {
        const result = await this.upload({ ...params, file });
        succeeded.push({ ...result, fileName: file.name });
      } catch (error) {
        failed.push({
          fileName: file.name,
          error: error instanceof Error ? error.message : 'Upload failed',
        });
      }
    }

    return { succeeded, failed };
  },

  async getStatus(
    documentId: string,
    organizationId?: string | null,
    isSuperAdmin = false,
  ): Promise<GetKmsDocumentStatusResponse> {
    const query = new URLSearchParams();
    appendOrganizationScope(
      query,
      'organizationId',
      organizationId,
      isSuperAdmin,
    );
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await fetch(
      buildApiUrl(`/api/v1/kms/documents/${documentId}/status${suffix}`),
      {
        method: 'GET',
        credentials: 'include',
      },
    );
    return parseJson<GetKmsDocumentStatusResponse>(response);
  },

  async updateExtractedFields(params: {
    documentId: string;
    extractedFields: Record<string, unknown>;
    organizationId?: string | null;
    isSuperAdmin?: boolean;
  }): Promise<GetKmsDocumentStatusResponse> {
    const query = new URLSearchParams();
    appendOrganizationScope(
      query,
      'organizationId',
      params.organizationId,
      Boolean(params.isSuperAdmin),
    );
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await fetch(
      buildApiUrl(
        `/api/v1/kms/documents/${params.documentId}/extracted-fields${suffix}`,
      ),
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ extracted_fields: params.extractedFields }),
      },
    );
    return parseJson<GetKmsDocumentStatusResponse>(response);
  },

  async confirmExtractedFields(params: {
    documentId: string;
    extractedFields?: Record<string, unknown>;
    organizationId?: string | null;
    isSuperAdmin?: boolean;
  }): Promise<{
    documentId: string;
    status: 'linked';
    fieldsConfirmedAt?: string | null;
  }> {
    const query = new URLSearchParams();
    appendOrganizationScope(
      query,
      'organizationId',
      params.organizationId,
      Boolean(params.isSuperAdmin),
    );
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await fetch(
      buildApiUrl(
        `/api/v1/kms/documents/${params.documentId}/confirm-fields${suffix}`,
      ),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          params.extractedFields !== undefined
            ? { extracted_fields: params.extractedFields }
            : {},
        ),
      },
    );
    return parseJson(response);
  },

  async listHitlQueue(params: {
    page: number;
    limit: number;
    organizationId?: string | null;
    isSuperAdmin?: boolean;
    q?: string;
  }): Promise<ListKmsHitlQueueResponse> {
    const query = new URLSearchParams({
      page: String(params.page),
      limit: String(params.limit),
    });
    appendOrganizationScope(
      query,
      'organizationId',
      params.organizationId,
      Boolean(params.isSuperAdmin),
    );
    if (params.q?.trim()) {
      query.set('q', params.q.trim());
    }
    const response = await fetch(
      buildApiUrl(`/api/v1/kms/hitl-queue?${query.toString()}`),
      {
        method: 'GET',
        credentials: 'include',
      },
    );
    return parseJson<ListKmsHitlQueueResponse>(response);
  },

  async listLinkedDocuments(params: {
    page: number;
    limit: number;
    organizationId?: string | null;
    assetId?: string | null;
    isSuperAdmin?: boolean;
    q?: string;
    documentType?: string;
    status?: string;
  }): Promise<ListLinkedKmsDocumentsResponse> {
    const query = new URLSearchParams({
      page: String(params.page),
      limit: String(params.limit),
    });
    appendOrganizationScope(
      query,
      'organizationId',
      params.organizationId,
      Boolean(params.isSuperAdmin),
    );
    if (params.assetId === null) {
      query.set('assetId', 'none');
    } else if (params.assetId) {
      query.set('assetId', params.assetId);
    }
    if (params.q?.trim()) {
      query.set('q', params.q.trim());
    }
    if (params.documentType?.trim()) {
      query.set('documentType', params.documentType.trim());
    }
    if (params.status?.trim()) {
      query.set('status', params.status.trim());
    }
    const response = await fetch(
      buildApiUrl(`/api/v1/kms/documents?${query.toString()}`),
      {
        method: 'GET',
        credentials: 'include',
      },
    );
    return parseJson<ListLinkedKmsDocumentsResponse>(response);
  },

  async resolveHitl(
    documentId: string,
    body: ResolveKmsHitlRequest,
    organizationId?: string | null,
    isSuperAdmin = false,
  ): Promise<ResolveKmsHitlResponse> {
    const query = new URLSearchParams();
    appendOrganizationScope(
      query,
      'organizationId',
      organizationId,
      isSuperAdmin,
    );
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await fetch(
      buildApiUrl(`/api/v1/kms/hitl-queue/${documentId}/resolve${suffix}`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return parseJson<ResolveKmsHitlResponse>(response);
  },

  async updateAssetLink(params: {
    documentId: string;
    assetId: string | null;
    isGlobal: boolean;
    organizationId?: string | null;
    isSuperAdmin?: boolean;
  }): Promise<{
    documentId: string;
    status: 'linked';
    assetId?: string | null;
  }> {
    const query = new URLSearchParams();
    appendOrganizationScope(
      query,
      'organizationId',
      params.organizationId,
      Boolean(params.isSuperAdmin),
    );
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await fetch(
      buildApiUrl(
        `/api/v1/kms/documents/${params.documentId}/asset-link${suffix}`,
      ),
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          asset_id: params.assetId,
          is_global: params.isGlobal,
        }),
      },
    );
    return parseJson(response);
  },

  async rerunAssetLink(params: {
    documentId: string;
    organizationId?: string | null;
    isSuperAdmin?: boolean;
  }): Promise<{ documentId: string; status: 'accepted' }> {
    const query = new URLSearchParams();
    appendOrganizationScope(
      query,
      'organizationId',
      params.organizationId,
      Boolean(params.isSuperAdmin),
    );
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await fetch(
      buildApiUrl(
        `/api/v1/kms/documents/${params.documentId}/rerun-link${suffix}`,
      ),
      {
        method: 'POST',
        credentials: 'include',
      },
    );
    return parseJson(response);
  },

  /**
   * Fetch the original uploaded file. When `inline` is true, opens a preview
   * tab for PDFs/images; otherwise triggers a download.
   */
  async openContent(params: {
    documentId: string;
    organizationId?: string | null;
    isSuperAdmin?: boolean;
    inline?: boolean;
  }): Promise<void> {
    const query = new URLSearchParams();
    appendOrganizationScope(
      query,
      'organizationId',
      params.organizationId,
      Boolean(params.isSuperAdmin),
    );
    if (params.inline) {
      query.set('inline', 'true');
    }
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await fetch(
      buildApiUrl(
        `/api/v1/kms/documents/${params.documentId}/content${suffix}`,
      ),
      {
        method: 'GET',
        credentials: 'include',
      },
    );
    if (!response.ok) {
      let message = `Request failed (${response.status})`;
      try {
        const body = (await response.json()) as { message?: string | string[] };
        if (Array.isArray(body.message)) {
          message = body.message.join(', ');
        } else if (typeof body.message === 'string') {
          message = body.message;
        }
      } catch {
        // ignore
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const fileNameMatch = /filename="?([^";]+)"?/i.exec(disposition);
    const fileName = fileNameMatch?.[1] ?? 'document';

    if (params.inline) {
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      // Revoke later so the preview tab can load the blob.
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  },
};
